/**
 * src/pages/Reports.jsx
 *
 * Reports page — fully wired to Redux.
 * Removed all kanbanService calls; data now comes from the real
 * /api/reports/* endpoints via Redux thunks.
 *
 * STYLING NOTE: Only the visual layer for the "Status" table (and the
 * shared bits it reuses — AvatarGroup, PriorityBadge, ProgressBar,
 * Pagination, table header/footer) was restyled to match the supplied
 * design. No Redux logic, selectors, thunks, or handlers were changed.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  ArrowRight,
  TrendingUp,
  Calendar,
  Clock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  X,
  UserRound,
  CalendarDays,
  Flag,
  Hourglass,
  ClipboardCheck,
  SquareCheckBig,
  Construction,
  Download,
  Loader,
  FileText,
} from "lucide-react";

import {
  fetchReportsOverview,
  fetchReportProjects,
  fetchProjectSummary,
  fetchProjectStatus,
  fetchProjectTimesheet,
  fetchProjectRisk,
  fetchTaskDetail,
  clearProjectDetail,
  clearTaskDetail,
} from "../store/slices/reportsSlice";
import { fetchDashboard } from "../store/slices/dashboardSlice";
import { authFetch } from "../lib/api";
import Icons from "../components/icons";
import TaskTimesheetPanel from "./Tasktimesheetpanel";


// ════════════════════════════════════════════════════════════════════
// SELECTORS  — keep components clean
// ════════════════════════════════════════════════════════════════════
const selectOverview         = (s) => s.reports.overview;
const selectProjects         = (s) => s.reports.projects;
const selectProjectSummary   = (s) => s.reports.projectSummary;
const selectProjectStatus    = (s) => s.reports.projectStatus;
const selectProjectTimesheet = (s) => s.reports.projectTimesheet;
const selectProjectRisk      = (s) => s.reports.projectRisk;
const selectTaskDetail       = (s) => s.reports.taskDetail;
const selectDashboardStats   = (s) => s.dashboard.stats;
const selectDashboardProjects = (s) => s.dashboard.projects;

// ════════════════════════════════════════════════════════════════════
// SMALL UTILITIES
// ════════════════════════════════════════════════════════════════════
const formatReportDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};
const formatShortDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    month: "short", day: "2-digit",
  });
};
const getWeekDateRangeLabel = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${String(monday.getDate()).padStart(2,"0")} ${monthNames[monday.getMonth()]} - ${String(sunday.getDate()).padStart(2,"0")} ${monthNames[sunday.getMonth()]} , ${monday.getFullYear()}`;
};

const clampPct = (value) => Math.max(0, Math.min(100, Number(value) || 0));
const getInitials = (name = "") => name.trim().charAt(0).toUpperCase() || "?";


const getTaskCompletion = (task) => {
  if (!task) return 0;
  if (task.completion != null) return clampPct(task.completion);
  if (task.progress != null) return clampPct(task.progress);
  if (task.completionPct != null) return clampPct(task.completionPct);
  if (task.completed) return 100;
  return 0;
};

const getSubtaskSummary = (task) => {
  const raw = task?.subtasks;
  if (Array.isArray(raw)) {
    const completed = raw.filter((s) => s.isCompleted || s.completed || s.status === "completed").length;
    return { completed, total: raw.length, display: `${completed}/${raw.length}` };
  }

  if (raw && typeof raw === "object") {
    const completed = raw.completed ?? raw.done ?? raw.subtasksDone ?? raw.completedCount ?? 0;
    const total = raw.total ?? raw.count ?? raw.subtasksTotal ?? raw.totalCount ?? 0;
    return { completed, total, display: raw.display || `${completed}/${total}` };
  }

  const completed = task?.subtasksDone ?? task?.completedSubtasks ?? task?.subtasksCompleted ?? 0;
  const total = task?.subtasksTotal ?? task?.totalSubtasks ?? task?.subtaskCount ?? 0;
  return { completed, total, display: `${completed}/${total}` };
};

const FONT = "'Poppins', sans-serif";

const REPORTS_RESPONSIVE_STYLES = `
  .reports-page { box-sizing: border-box; width: 100%; }
  .reports-stats { display: grid !important; grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .reports-table-scroll { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
  .reports-data-table { min-width: 680px; }
  /* Task details remain part of the report layout, so opening one makes the
     report table resize instead of overlaying the page. */
  .reports-page--with-details { padding-right: 0 !important; }
  .reports-project-layout--with-details { grid-template-columns: minmax(0, 1fr) minmax(0, 467px) !important; }
  .reports-task-details-column { min-width: 0; align-self: stretch; display: flex; }
  .reports-task-details-card { width: 100%; height: 100% !important; min-height: 0 !important; }
  @media (max-width: 1100px) {
    .reports-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (max-width: 767px) {
    .reports-page { padding: 16px !important; }
    .reports-header { padding: 20px !important; }
    .reports-header-row, .reports-leave-header { align-items: flex-start !important; flex-direction: column !important; }
    .reports-header-row > button, .reports-leave-controls, .reports-leave-controls > div, .reports-leave-controls button { width: 100% !important; max-width: none !important; }
    .reports-picker-area { padding: 16px !important; gap: 16px !important; }
    .reports-picker-area > div { min-width: 0 !important; max-width: none !important; width: 100% !important; margin-left: 0 !important; }
    .reports-stats { grid-template-columns: 1fr; gap: 12px !important; }
    .reports-report-card { padding: 24px !important; }
    .reports-table-card-head, .reports-table-body { padding-left: 16px !important; padding-right: 16px !important; }
    .reports-tabs { gap: 20px !important; overflow-x: auto; }
    .reports-pagination { align-items: flex-start !important; flex-direction: column !important; gap: 12px !important; padding: 16px !important; }
    .reports-leave-body { padding: 16px !important; }
    .reports-task-drawer { top: 0 !important; right: 0 !important; bottom: 0 !important; width: 100% !important; }
  }
`;

const escapeExcelCell = (value) =>
  String(value ?? "-")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const makeFileSafe = (value) =>
  String(value || "task_details")
    .trim()
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase() || "task_details";

const csvCell = (value) => {
  const normalized = Array.isArray(value)
    ? value.map((item) => item?.name || item?.email || item).join(", ")
    : value ?? "";
  return `"${String(normalized).replace(/"/g, '""')}"`;
};

const downloadCSV = (filename, headers, rows) => {
  const content = [headers.map(csvCell).join(","), ...rows.map((row) => row.map(csvCell).join(","))].join("\n");
  const url = window.URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const downloadProjectReportAsCSV = (report) => {
  const tab = report?.tab || "status";
  const rows = report?.rows || [];
  const configurations = {
    timesheet: {
      headers: ["Task", "Estimated Hours", "Delay Hours", "Auto Submitted"],
      values: (row) => [row.task || row.name, row.estimatedHours, row.delayHours, row.autoSubmitted ? "Yes" : "No"],
    },
    risk: {
      headers: ["Task", "Resources", "Due Date", "Days Overdue", "Impact", "Priority", "Status"],
      values: (row) => [row.name || row.task, row.resources || row.assignees, row.dueDateDisplay || formatReportDate(row.dueDate), row.daysOverdueDisplay || row.daysOverdue, row.impact, row.priority, row.status],
    },
    status: {
      headers: ["Task", "Resources", "Due Date", "Status", "Priority", "Completion"],
      values: (row) => [row.name || row.task, row.resources || row.assignees, row.dueDateDisplay || formatReportDate(row.dueDate), row.status, row.priority, `${row.completion ?? 0}%`],
    },
  };
  const config = configurations[tab] || configurations.status;
  downloadCSV(
    `${makeFileSafe(report?.project?.name || "project_report")}_${tab}.csv`,
    config.headers,
    rows.map(config.values),
  );
};

const downloadTaskAsExcel = (task, projectName) => {
  const completion = getTaskCompletion(task);
  const subtasks = getSubtaskSummary(task);
  const overdue = task?.isOverdue || task?.status === "At Risk";
  const status = task?.status || (task?.completed ? "Completed" : overdue ? "At Risk" : "On Track");
  const resources = (task?.resources || [])
    .map((resource) => resource.name || resource.email)
    .filter(Boolean)
    .join(", ") || "-";

  const rows = [
    ["Task", task?.name || task?.task],
    ["Project", projectName || "Unknown Project"],
    ["Resources", resources],
    ["Due Date", formatReportDate(task?.dueDate || task?.taskDeadline)],
    ["Priority", (task?.priority || "-").toUpperCase()],
    ["Status", status],
    ["Completion", `${completion}%`],
    ["Sub Tasks", subtasks?.display || `${subtasks?.completed || 0}/${subtasks?.total || 0}`],
    ["Task Type", task?.taskType || "Not Set"],
    ["Start Date", formatReportDate(task?.startDate || task?.taskStartDate)],
    ["Created On", formatReportDate(task?.createdAt || task?.createdOn)],
    ["Created By", task?.createdBy?.name || task?.createdBy || task?.creator?.name || "-"],
  ];

  const tableRows = rows
    .map(([label, value]) => `<tr><th style="text-align:left;background:#eef2ff;padding:8px;border:1px solid #d9e2ff;">${escapeExcelCell(label)}</th><td style="padding:8px;border:1px solid #d9e2ff;">${escapeExcelCell(value)}</td></tr>`)
    .join("");

  const workbook = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8" /></head><body><table>${tableRows}</table></body></html>`;
  const blob = new Blob([workbook], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", `${makeFileSafe(task?.name || task?.task)}_details.xls`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const downloadLeaveReportAsExcel = (rows, year, resourceLabel) => {
  const headerCells = ["Employee", "From", "To", "Days", "Type", "Status"]
    .map((h) => `<th style="text-align:left;background:#eef2ff;padding:8px;border:1px solid #d9e2ff;">${h}</th>`)
    .join("");

  const bodyRows = (rows || [])
    .map((row) => {
      const cells = [
        row.user?.name || row.userName || row.name || "-",
        formatReportDate(row.fromDate || row.from),
        formatReportDate(row.toDate || row.to),
        row.totalDays ?? row.days ?? "-",
        row.leaveType || row.type || "-",
        row.status || "-",
      ];
      return `<tr>${cells.map((c) => `<td style="padding:8px;border:1px solid #d9e2ff;">${escapeExcelCell(c)}</td>`).join("")}</tr>`;
    })
    .join("");

  const workbook = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8" /></head><body><table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table></body></html>`;
  const blob = new Blob([workbook], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", `${makeFileSafe(`leave_report_${resourceLabel}_${year}`)}.xls`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ════════════════════════════════════════════════════════════════════
// SHARED UI COMPONENTS
// ════════════════════════════════════════════════════════════════════
const StatCard = ({ label, value, sub, borderColor, icon, compact = false, onClick }) => (  <div
    className="reports-stat-card"
    onClick={onClick}
    style={{
      position: "relative",
      width: "100%",
      minWidth: 200,
      height: 141,
      flex: 1,
      background: "#FFFFFF",
      boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.001)",
      borderRadius: 8,
      overflow: "hidden",
      cursor: onClick ? "pointer" : "default",
    }}
  >
    {/* Rectangle 1 — left color bar */}
    <div
      style={{
        position: "absolute",
        width: 4,
        height: 141,
        left: 0,
        top: 0,
        background: borderColor,
      }}
    />

    {/* Label */}
    <p
      style={{
        position: "absolute",
        left: 16,
        top: 16,
        margin: 0,
        fontFamily: "'Poppins', sans-serif",
        fontStyle: "normal",
        fontWeight: 500,
        fontSize: 14,
        lineHeight: "21px",
        display: "flex",
        alignItems: "center",
        color: "#000000",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </p>

    {/* Value */}
    <p
      style={{
        position: "absolute",
        left: 16,
        top: 63,
        margin: 0,
        fontFamily: "'Work Sans', sans-serif",
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: 32,
        lineHeight: "38px",
        display: "flex",
        alignItems: "center",
        color: "#000000",
      }}
    >
      {value}
    </p>

{/* Icon — only rendered if provided */}
    {icon && (
      <div
        style={{
          position: "absolute",
          left: 16,
          top: 105,
          width: 20,
          height: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: 0.5,
          color: "#000000",
        }}
      >
        {icon}
      </div>
    )}

    {/* Sub label — shifts left to 16 when there's no icon */}
    <p
      style={{
        position: "absolute",
        left: icon ? 44 : 16,
        top: 106,
        margin: 0,
        fontFamily: "'Poppins', sans-serif",
        fontStyle: "normal",
        fontWeight: 400,
        fontSize: 12,
        lineHeight: "18px",
        display: "flex",
        alignItems: "center",
        color: "#000000",
        opacity: 0.5,
        whiteSpace: "nowrap",
      }}
    >
      {sub}
    </p>
  </div>
);
// Match the Resources pagination controls.
const Pagination = ({ current, total, onChange }) => {
  const arrowStyle = (disabled) => ({
    width: 40, height: 40, borderRadius: 8, border: "1px solid #ECEFF5",
    background: "#F5F5F5", display: "flex", alignItems: "center",
    justifyContent: "center", cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <button onClick={() => onChange(current - 1)} disabled={current === 1} style={arrowStyle(current === 1)}>
        <ChevronLeft size={16} color="#374151" />
      </button>
      {Array.from({ length: total }, (_, i) => i + 1).map((p) => (
        <button key={p} onClick={() => onChange(p)}
          style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: current === p ? "#4C5CE7" : "transparent", color: current === p ? "#fff" : "#000", fontWeight: 400, fontSize: 12, fontFamily: FONT, cursor: "pointer" }}>
          {p}
        </button>
      ))}
      <button onClick={() => onChange(current + 1)} disabled={current === total} style={arrowStyle(current === total)}>
        <ChevronRight size={16} color="#374151" />
      </button>
    </div>
  );
};

// Restyled: overlapping circular avatars with a person glyph + a
// "+N" circle for the remainder, matching the design's avatar group.
// Replace the existing AvatarGroup component with this
const AvatarGroup = ({ assignees = [] }) => {
  const [open, setOpen] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const visible = assignees.slice(0, 2);
  const extra = assignees.length - visible.length;

const renderAvatarImg = (a, size = 32) => {
    const photo = a.avatar || a.profileImage || a.photo || a.image;
    if (photo) {
      return (
        <img
          src={photo}
          alt={a.name || ""}
          style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }}
        />
      );
    }
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: "#F0F0F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: size * 0.4,
          fontWeight: 600,
          color: "#7F7F7F",
          fontFamily: FONT,
        }}
      >
        {getInitials(a.name)}
      </div>
    );
  };

  if (!visible.length) {
    return (
      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <UserRound size={16} color="#000" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Show assigned resource names"
        style={{ display: "flex", alignItems: "center", cursor: "pointer", padding: 0, border: "none", background: "transparent" }}
      >
        {visible.map((a, i) => (
          <div key={a._id || i} title={a.name || ""}
            style={{ border: "2px solid #fff", borderRadius: "50%", marginLeft: i > 0 ? -10 : 0 }}>
            {renderAvatarImg(a)}
          </div>
        ))}
        {extra > 0 && (
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.06)", marginLeft: -10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 400, color: "#000", fontFamily: FONT }}>
            +{extra}
          </div>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            right: "calc(100% + 12px)",
            transform: "translateY(-50%)",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            padding: "10px 14px",
            minWidth: 220,
            zIndex: 50,
          }}
        >
          {assignees.map((a, i) => (
            <div key={a._id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
              {renderAvatarImg(a, 32)}
            <span style={{ fontSize: 14, color: "#111", fontFamily: FONT }}>
  {a.name || a.fullName || a.username || `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email || "Unknown"}
</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Restyled: pill badge, peach background + red text for HIGH (matches
// design exactly). Other levels keep a sensible analogous look.
const PriorityBadge = ({ level }) => {
  const map = {
    HIGH:     { bg: "#FFCA75CC", color: "#FF1A00" },
    MEDIUM:   { bg: "#FFE8B6", color: "#B76E00" },
    LOW:      { bg: "#CFFFDE", color: "#003C18" },
    CRITICAL: { bg: "#FFD6D6", color: "#D60000" },
  };
  const key = (level || "").toUpperCase();
  const c = map[key] || map.HIGH;
  return (
    <span style={{ background: c.bg, color: c.color, fontSize: 12, fontWeight: 400, fontFamily: FONT, padding: "4px 14px", borderRadius: 999, textTransform: "capitalize", display: "inline-block" }}>
      {key || "—"}
    </span>
  );
};




const ImpactBadge = ({ level }) => {
  const map = { HIGH: { bg: "#FFCA75CC", color: "#FF1A00" }, MEDIUM: { bg: "#FFE8B6", color: "#B76E00" }, LOW: { bg: "#CFFFDE", color: "#003C18" } };
  const c = map[level] || map.LOW;
  return <span style={{ background: c.bg, color: c.color, fontSize: 12, fontWeight: 400, fontFamily: FONT, padding: "4px 14px", borderRadius: 999, textTransform: "capitalize", display: "inline-block"}}>{level}</span>;
};
const InsightIcon = ({ bg, children }) => (
  <div style={{ width: 40, height: 40, borderRadius: 24, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    {children}
  </div>
);

const InsightBadge = ({ bg, color, children }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px 7px", background: bg, color, borderRadius: 4, fontFamily: FONT, fontWeight: 400, fontSize: 12, lineHeight: "18px", textTransform: "capitalize", whiteSpace: "nowrap" }}>
    {children}
  </span>
);

const InsightRow = ({ icon, iconBg, text, badgeText, badgeBg, badgeColor, isLast }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "17px 0", borderBottom: isLast ? "none" : "1px solid rgba(0,0,0,0.05)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <InsightIcon bg={iconBg}>{icon}</InsightIcon>
      <span style={{ fontFamily: FONT, fontWeight: 400, fontSize: 14, lineHeight: "21px", color: "rgba(0,0,0,0.5)", textTransform: "lowercase" }}>{text}</span>
    </div>
    <InsightBadge bg={badgeBg} color={badgeColor}>{badgeText}</InsightBadge>
  </div>
);

const INSIGHT_COLORS = {
  green: { bg: "#EDFAF3", color: "#2C9378" },
  orange: { bg: "#F8EDE5", color: "#F66D2F" },
  red: { bg: "#FBECEC", color: "#FF0000" },
};

const getProjectRiskInsight = (count) => {
  if (count === 0) return { label: "Stable", tone: "green" };
  if (count <= 3) return { label: "Needs Attention", tone: "orange" };
  return { label: "High Priority", tone: "red" };
};

const getOverdueInsight = (count) => {
  if (count === 0) return { label: "On Schedule", tone: "green" };
  if (count <= 5) return { label: "Needs Attention", tone: "orange" };
  return { label: "Critical", tone: "red" };
};

const getTimesheetInsight = (rate) => {
  if (rate >= 90) return { label: "On Track", tone: "green" };
  if (rate >= 70) return { label: "Moderate", tone: "orange" };
  return { label: "Delayed", tone: "red" };
};

const ReportCard = ({ iconBg, icon, title, description, rows, buttonColor, onView }) => (
  <div className="reports-report-card" style={{ background: "#fff", borderRadius: 16, boxShadow: "0px 0px 4px rgba(0,0,0,0.05)", padding: "40px 56px" }}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 28, marginBottom: 38 }}>
      <div style={{ width: 106, height: 102, borderRadius: 8, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <h2 style={{ margin: 0, fontFamily: FONT, fontWeight: 500, fontSize: 24, lineHeight: "36px", textTransform: "capitalize", color: "#000" }}>{title}</h2>
        <p style={{ margin: "12px 0 0", fontFamily: FONT, fontWeight: 400, fontSize: 16, lineHeight: "24px", textTransform: "capitalize", color: "rgba(0,0,0,0.5)" }}>{description}</p>
      </div>
    </div>
    <p style={{ margin: "0 0 16px", paddingBottom: 16, borderBottom: "1px solid rgba(0,0,0,0.05)", fontFamily: FONT, fontWeight: 500, fontSize: 16, lineHeight: "24px", textTransform: "capitalize", color: "rgba(0,0,0,0.5)" }}>
      KEY INSIGHTS
    </p>
    <div>
      {rows.map((row, i) => <InsightRow key={i} {...row} isLast={i === rows.length - 1} />)}
    </div>
    <button onClick={() => onView()} style={{ width: "100%", marginTop: 38, height: 40, border: `1px solid ${buttonColor}`, borderRadius: 8, background: "#fff", color: buttonColor, fontFamily: FONT, fontWeight: 400, fontSize: 14, lineHeight: "21px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
      View Report <ArrowRight size={16} color={buttonColor} />
    </button>
  </div>
);
// Restyled: percentage label on the left, then a light gray track with
// a blue fill (matches the design's completion column).
const ProgressBar = ({ pct }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span style={{ fontSize: 13, color: "#000", fontWeight: 400, fontFamily: "Poppins", minWidth: 32 }}>{pct}%</span>
    <div style={{ width: 80, height: 7, background: "rgba(198, 198, 198, 0.36)", borderRadius: 68, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: "#528CF9", borderRadius: 68 }} />
    </div>
  </div>
);
const LeaveBadge = ({ status }) => {
  const map = {
    APPROVED: { bg: "rgba(34, 197, 94, 0.45)", color: "#006B0B" },
    REJECTED: { bg: "#FFABAB", color: "#5E0000" },
    PENDING:  { bg: "#FEF2D4", color: "#7C4A10" },
  };
  const c = map[status] || map.PENDING;
  return <span style={{ background: c.bg, color: c.color, fontSize: 12, fontWeight: 400, fontFamily: FONT, padding: "1px 12px", borderRadius: 32, display: "inline-flex", alignItems: "center", height: 20 }}>{status}</span>;
};

const LoadingSpinner = ({ text = "Loading..." }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "48px 0", color: "#9ca3af" }}>
    <Loader size={20} style={{ animation: "spin 1s linear infinite" }} />
    <span style={{ fontSize: 14 }}>{text}</span>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ════════════════════════════════════════════════════════════════════
// TASK DETAILS PANEL
// ════════════════════════════════════════════════════════════════════
const TaskDetailsPanel = ({ task, projectName, onClose }) => {
  const overdue = task?.isOverdue || task?.status === "At Risk";
  const status = task?.status || (task?.completed ? "Completed" : overdue ? "At Risk" : "On Track");
  const statusColor = status === "Completed" ? "#22c55e" : status === "At Risk" ? "#ef4444" : "#528CF9";
  const completion = getTaskCompletion(task);
  const subtasks = getSubtaskSummary(task);

  const createdAt = task?.createdAt || task?.created_on || task?.createdDate;
  const createdBy = task?.createdBy?.name || task?.createdBy?.fullName || task?.createdBy || task?.creator?.name || "-";
  const rows = [
    { icon: <UserRound size={20} strokeWidth={1.8} />, label: "Resources", value: <AvatarGroup assignees={task?.resources || []} /> },
    { icon: <CalendarDays size={20} strokeWidth={1.8} />, label: "Due Date", value: formatReportDate(task?.dueDate || task?.taskDeadline) },
    { icon: <SquareCheckBig size={20} strokeWidth={1.8} />, label: "Priority", value: <PriorityBadge level={(task?.priority || "").toUpperCase()} /> },
    {
      icon: <Flag size={20} strokeWidth={1.8} />, label: "Status",
      value: <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: statusColor }} />{status}</span>,
    },
    {
      icon: <Hourglass size={20} strokeWidth={1.8} />, label: "Completion",
      value: (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 120, height: 7, borderRadius: 99, background: "#e8e8e8", overflow: "hidden" }}>
            <div style={{ width: `${completion}%`, height: "100%", borderRadius: 99, background: "#4253C7" }} />
          </div>
          <span>{completion}%</span>
        </div>
      ),
    },
    { icon: <ClipboardCheck size={20} strokeWidth={1.8} />, label: "Sub Tasks", value: subtasks?.display || `${subtasks?.completed || 0}/${subtasks?.total || 0}` },
    { icon: <Construction size={20} strokeWidth={1.8} />, label: "Task Type", value: task?.taskType || "Not Set" },
    { icon: <CalendarDays size={20} strokeWidth={1.8} />, label: "Created On", value: createdAt ? formatReportDate(createdAt) : "-" },
    { icon: <UserRound size={20} strokeWidth={1.8} />, label: "Created By", value: createdBy },
  ];

  return (
    <aside className="reports-task-details-card" style={{ boxSizing: "border-box", width: "100%", background: "#fff", borderRadius: 16, padding: 24, display: "flex", flexDirection: "column", boxShadow: "0 0 4px rgba(0,0,0,0.05)", fontFamily: FONT }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 500, color: "#111" }}>Task Details</h2>
          <button type="button" onClick={onClose} aria-label="Close task details" style={{ border: "none", background: "transparent", color: "rgba(0,0,0,0.5)", cursor: "pointer", padding: 0, display: "flex" }}><X size={24} /></button>
        </div>
        <div style={{ borderBottom: "1px solid rgba(127,127,127,0.07)", padding: "6px 0 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 18 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "#111" }}>{task?.name || task?.task}</h3>
              <p style={{ margin: "8px 0 0", fontSize: 14, color: "#7F7F7F" }}>Project :</p>
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: "rgba(0,0,0,0.6)" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: statusColor }} />{status}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: 14, color: "#111" }}>{projectName || "Unknown Project"}</p>
            </div>
          </div>
        </div>
        <h3 style={{ margin: "18px 0 30px", fontSize: 20, fontWeight: 500, color: "#111" }}>Status Details</h3>
      <div style={{ display: "grid", gap: 27, borderBottom: "1px solid rgba(127,127,127,0.07)", paddingBottom: 24 }}>
          {rows.map((row) => (
            <div key={row.label} style={{ display: "grid", gridTemplateColumns: "20px 1fr auto", alignItems: "center", gap: 12, color: "#7F7F7F", fontSize: 14 }}>
              <span style={{ display: "flex", color: "#7F7F7F" }}>{row.icon}</span>
              <span>{row.label}</span>
              <span style={{ color: "#111", fontSize: 14, textAlign: "right" }}>{row.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 18, display: "grid", gap: 10, flexShrink: 0 }}>
  <button type="button" onClick={() => downloadTaskAsExcel(task, projectName)}
    style={{ width: "100%", height: 46, border: "none", borderRadius: 12, background: "#4253C7", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: FONT }}>
    <Download size={20} strokeWidth={2} />
    Download
  </button>
  <button type="button" onClick={onClose}
    style={{ width: "100%", height: 46, border: "1.5px solid #d1d5db", borderRadius: 12, background: "#fff", color: "#6b7280", fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: FONT }}>
    Cancel
  </button>
</div>
    </aside>
  );
};

// ════════════════════════════════════════════════════════════════════
// MAIN REPORTS OVERVIEW
// ══════════════════════════════════════════════════════════════════
const ReportsMain = ({ onProjectReport, onLeaveReport }) => {
  const dispatch = useDispatch();
  const { data: overviewData, loading } = useSelector(selectOverview);
  const { data: projects } = useSelector(selectProjects);
  const dashboardStats = useSelector(selectDashboardStats);
  const dashboardProjects = useSelector(selectDashboardProjects);
  const reportProjects = dashboardProjects.length ? dashboardProjects : projects;

  const [leaveStats, setLeaveStats]         = useState(null);
  const [leaveBalance, setLeaveBalance]     = useState(null);
  const [upcomingHoliday, setUpcomingHoliday] = useState(null);

  useEffect(() => {
    // Use the same request and state that powers Dashboard's cards. This
    // keeps shared figures available even if report-only aggregation fails.
    dispatch(fetchDashboard());
    dispatch(fetchReportsOverview());

    (async () => {
      try {
        const [statsRes, balanceRes, holidayRes] = await Promise.all([
          authFetch("/api/leaves/stats"),
          authFetch("/api/leaves/balance"),
          authFetch("/api/holidays/upcoming"),
        ]);
        const [statsJson, balanceJson, holidayJson] = await Promise.all([
          statsRes.json(),
          balanceRes.json(),
          holidayRes.json(),
        ]);
        if (statsJson?.data)   setLeaveStats(statsJson.data);
        if (balanceJson?.data) setLeaveBalance(balanceJson.data);
        if (holidayJson?.data) setUpcomingHoliday(holidayJson.data);
      } catch (err) {
      }
    })();
  }, [dispatch]);

  const stats = {
    ...(overviewData || {}),
    // Dashboard is authoritative for project-level card values.
    totalProjects: dashboardStats?.totalProjects ?? overviewData?.totalProjects ?? reportProjects.length,
    atRiskProjects: dashboardStats?.atRisk ?? overviewData?.atRiskProjects,
  };

  // This is the same authoritative count used by Dashboard's At Risk card.
  // Only fall back to the project list while the dashboard response is absent.
  const atRiskProjectsCount = stats.atRiskProjects ?? reportProjects.filter(
    (project) => project.trackingStatus === "at-risk",
  ).length;
  const overdueTaskCount = Number(stats.pendingTasks) || 0;
  const timesheetRate = Number(stats.timesheetRate) || 0;
  const projectRiskInsight = getProjectRiskInsight(atRiskProjectsCount);
  const overdueInsight = getOverdueInsight(overdueTaskCount);
  const timesheetInsight = getTimesheetInsight(timesheetRate);



    const pendingCount = leaveStats?.pendingCount ?? leaveStats?.pending ?? 0;
  const avgBalance   = leaveBalance?.avgBalance ?? leaveBalance?.average ?? leaveBalance?.balance ?? "-";
  const holidayName  = upcomingHoliday?.name ?? "-";
  const holidayDate  = upcomingHoliday?.date
    ? new Date(upcomingHoliday.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
    : "0";

const projectInsights = [
    { icon: <Icons.R1 />, iconBg: INSIGHT_COLORS[projectRiskInsight.tone].bg, text: `${atRiskProjectsCount} projects are at risk`, badgeText: projectRiskInsight.label, badgeBg: INSIGHT_COLORS[projectRiskInsight.tone].bg, badgeColor: INSIGHT_COLORS[projectRiskInsight.tone].color },
    { icon: <Icons.R2 />, iconBg: INSIGHT_COLORS[overdueInsight.tone].bg, text: `${overdueTaskCount} tasks overdue`, badgeText: overdueInsight.label, badgeBg: INSIGHT_COLORS[overdueInsight.tone].bg, badgeColor: INSIGHT_COLORS[overdueInsight.tone].color },
    { icon: <Clock size={20} color={INSIGHT_COLORS[timesheetInsight.tone].color} />, iconBg: INSIGHT_COLORS[timesheetInsight.tone].bg, text: `timesheet submission rate : ${timesheetRate}%`, badgeText: timesheetInsight.label, badgeBg: INSIGHT_COLORS[timesheetInsight.tone].bg, badgeColor: INSIGHT_COLORS[timesheetInsight.tone].color },
  ];

  const leaveInsights = [
    { icon: <UserRound size={20} color="#FF0000" />, iconBg: "#FBECEC", text: `${pendingCount} pending approvals`, badgeText: pendingCount > 0 ? "Requires Action" : "All Clear", badgeBg: "#FBECEC", badgeColor: "#FF0000" },
    { icon: <Icons.ReportsArrow color="#F66D2F" />, iconBg: "#F8EDE5", text: `avg leave balance : ${avgBalance}`, badgeText: "Stable", badgeBg: "#F8EDE5", badgeColor: "#F66D2F" },
    { icon: <Calendar size={20} color="#2C9378" />, iconBg: "#EDFAF3", text: `upcoming break: ${holidayName} (${holidayDate})`, badgeText: "Scheduled", badgeBg: "#EDFAF3", badgeColor: "#2C9378" },
  ];
  return (
    <div className="reports-page"
      style={{
        padding: "28px 32px",
        minHeight: "100vh",
        background: "#f3f4f8",
      }}
    >
     <div className="reports-header"
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: 16,
          padding: "24px 28px",
          minHeight: 106,
          marginBottom: 24,
          boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.05)",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontFamily: FONT,
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: 24,
            lineHeight: "36px",
            textTransform: "capitalize",
            color: "#000000",
          }}
        >
          Reports
        </h1>
        <p
          style={{
            margin: "4px 0 0", fontFamily: FONT,fontStyle: "normal",
            fontWeight: 400,
            fontSize: 12,
            lineHeight: "18px",
            textTransform: "capitalize",
            color: "rgba(0, 0, 0, 0.5)",
          }}
        >
          Gain Insights Into Project Performance And Resource Utilization.
        </p>
      </div>

      {loading ? <LoadingSpinner text="Loading overview..." /> : (
        <>
          <div className="reports-stats" style={{ gap: 16, marginBottom: 24 }}>
<StatCard label="Total Projects"  value={stats.totalProjects  ?? "—"} sub="+3 this month"                  borderColor="white"    icon={<TrendingUp size={20} />} />
            <StatCard label="Pending Tasks"   value={stats.pendingTasks   ?? "—"} sub="Awaiting action"                borderColor="#513CF6"  icon={<Hourglass size={20} />} />
            <StatCard label="At Risk"         value={atRiskProjectsCount  ?? "—"} sub="Needs immediate attention"      borderColor="#F50B0B"  icon={<AlertTriangle size={20} />} />
            <StatCard label="Timesheet Delay" value={stats.timesheetDelay ?? "—"} sub="Auto-Submitted Entries"         borderColor="#22C55E"  icon={<Clock size={20} />} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            <ReportCard
              iconBg="#EEF1FF"
              icon={<Icons.ReportsArrow />}
              title="Project Report"
              description="Track Project Status, Pending Tasks, Timesheet Delays And Projects At Risk."
              rows={projectInsights}
              buttonColor="#4379FE"
              onView={onProjectReport}
            />
            <ReportCard
              iconBg="#EDFAF3"
              icon={<Icons.LeaveReport />}
              title="Leave Report"
              description="View Leave Balance And Summary Of Each Resource"
              rows={leaveInsights}
              buttonColor="#1DAA6E"
              onView={onLeaveReport}
            />
          </div> </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// PROJECT REPORT PAGE
// ════════════════════════════════════════════════════════════════════

// Heading label per tab, used for the "All Tasks(N)" style heading
// above the table (purely presentational — uses data already in Redux).
const TAB_LABELS = { Status: "All Tasks", Timesheet: "Timesheet", Risk: "At Risk Tasks" };

const ProjectReportPage = ({ onBack, initialTab = "Status" }) => {
  const dispatch = useDispatch();
  const { data: projects, loading: projectsLoading } =
    useSelector(selectProjects);
  const { data: summary } = useSelector(selectProjectSummary);
  const [timesheetTaskId, setTimesheetTaskId] = useState(null);
  const {
    data: statusData,
    pagination: statusPag,
    loading: statusLoading,
  } = useSelector(selectProjectStatus);
  const {
    data: timesheetData,
    pagination: timesheetPag,
    loading: timesheetLoading,
  } = useSelector(selectProjectTimesheet);
  const {
    data: riskData,
    pagination: riskPag,
    loading: riskLoading,
  } = useSelector(selectProjectRisk);
  const { data: taskDetail, loading: taskDetailLoading } =
    useSelector(selectTaskDetail);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [page, setPage] = useState(1);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const selectedProject = projects.find((p) => String(p._id || p.id) === String(selectedProjectId)) || projects[0] || null;
  const currentProjectId = selectedProject?._id || selectedProject?.id || "";
  const [weekDateRange] = useState(getWeekDateRangeLabel());
  // Set initial project when projects load
  useEffect(() => {
    if (projects.length && !selectedProjectId) {
      setSelectedProjectId(projects[0]._id || projects[0].id);
    }
  }, [projects, selectedProjectId]);

  // Fetch project summary + current tab whenever project changes
  useEffect(() => {
    if (!currentProjectId) return;
    dispatch(clearProjectDetail());
    dispatch(fetchProjectSummary(currentProjectId));
    loadTab(activeTab, 1, currentProjectId);
  }, [currentProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch task detail panel
  useEffect(() => {
    if (selectedTaskId && currentProjectId) {
      dispatch(fetchTaskDetail({ projectId: currentProjectId, taskId: selectedTaskId }));
    }
  }, [selectedTaskId, currentProjectId, dispatch]);
  // Scroll back to top whenever a task's details panel opens, so the
// panel header is visible instead of staying wherever the row was.
useEffect(() => {
  if (selectedTaskId || timesheetTaskId) {
    document.querySelector(".page-content")?.scrollTo({ top: 0, behavior: "smooth" });
  }
}, [selectedTaskId, timesheetTaskId]);

  const loadTab = useCallback((tab, pg = 1, projectId = currentProjectId) => {
    if (!projectId) return;
    setPage(pg);
    if (tab === "Status")    dispatch(fetchProjectStatus({ projectId, page: pg }));
    if (tab === "Timesheet") dispatch(fetchProjectTimesheet({ projectId, page: pg }));
    if (tab === "Risk")      dispatch(fetchProjectRisk({ projectId, page: pg }));
  }, [dispatch, currentProjectId]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedTaskId(null);
    loadTab(tab, 1);
    setTimesheetTaskId(null);
  };

  const handlePageChange = (pg) => loadTab(activeTab, pg);

  const handleProjectChange = (id) => {
    setSelectedProjectId(id);
    setPage(1);
    setActiveTab("Status");
    setSelectedTaskId(null);
    setTimesheetTaskId(null);
    setShowDropdown(false);
  };

  // Derive summary stats from redux
  const completionPct    = summary?.completionPct    ?? 0;
  const totalTasks       = summary?.totalTasks       ?? statusData?.length ?? 0;
  const completedTasks   = summary?.completedTasks   ?? 0;
  const pendingTasks     = summary?.pendingTasks      ?? 0;
  const atRiskCount      = summary?.atRiskTasks      ?? riskData?.length ?? 0;
  const resourceCount    = summary?.resourceCount    ?? 0;

  // Pagination
  const currentPag     = activeTab === "Status" ? statusPag : activeTab === "Timesheet" ? timesheetPag : riskPag;
  const totalPages     = currentPag?.totalPages ?? 1;
  const isTabLoading   = activeTab === "Status" ? statusLoading : activeTab === "Timesheet" ? timesheetLoading : riskLoading;

  const currentRows    = activeTab === "Status" ? statusData : activeTab === "Timesheet" ? timesheetData : riskData;
  const taskDrawerWidth = activeTab === "Timesheet" && timesheetTaskId ? 480 : selectedTaskId ? 467 : 0;

  // Table header/cell styles — restyled to match the design (light
  // gray capitalized labels, hairline row separators).
  const thStyle = { textAlign: "left", padding: "0 12px 16px 12px", fontSize: 13, fontWeight: 400, color: "rgba(0,0,0,0.5)", fontFamily: FONT, textTransform: "capitalize", borderBottom: "1px solid rgba(127,127,127,0.07)" };
  const tdStyle = { padding: "18px 12px", fontSize: 13, color: "#000", fontFamily: FONT, borderBottom: "1px solid rgba(127,127,127,0.07)" };
  const taskNameButtonStyle = {
    border: "none",
    background: "transparent",
    padding: 0,
    color: "#1f2937",
    font: "inherit",
    fontWeight: 500,
    cursor: "pointer",
    textAlign: "left",
  };
  const openTaskDetails = (event, taskId) => {
    event.stopPropagation();
    if (!taskId) return;
    setSelectedTaskId(taskId);
  };
  const handleExportCSV = async () => {
    if (!currentProjectId) return;

    try {
      const params = new URLSearchParams({ tab: activeTab.toLowerCase() });
      const response = await authFetch(`/api/reports/projects/${currentProjectId}/download?${params}`);
      const report = await response.json();
      if (!response.ok || !report?.success) throw new Error(report?.message || "Unable to export project report");
      downloadProjectReportAsCSV(report.data);
    } catch (error) {
      console.error("Unable to export the project CSV", error);
    }
  };

  const renderStatusRows = () => (
  <div className="reports-table-scroll" style={{ width: "100%", overflowX: "auto" }}>
    <table className="reports-data-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
  <colgroup>
    <col style={{ width: "22%" }} />
    <col style={{ width: "14%" }} />
    <col style={{ width: "16%" }} />
    <col style={{ width: "15%" }} />
    <col style={{ width: "14%" }} />
    <col style={{ width: "19%" }} />
  </colgroup>
      <thead><tr>
        <th style={thStyle}>Task</th><th style={{ ...thStyle, textAlign: "center" }}>Resources</th>
        <th style={{ ...thStyle, textAlign: "center" }}>Due Date</th><th style={{ ...thStyle, textAlign: "center" }}>Status</th>
        <th style={{ ...thStyle, textAlign: "center" }}>Priority</th><th style={{ ...thStyle, textAlign: "center" }}>Completion</th>

      </tr></thead>
      <tbody>
        {currentRows.map((task) => {
          const dot = task.status === "Completed" ? "#22c55e" : task.status === "At Risk" ? "#ef4444" : "#528CF9";
          const sel = selectedTaskId === task._id;
          return (
            <tr key={task._id} style={{ background: sel ? "#eef2ff" : "transparent" }}>
              <td style={tdStyle}>
                <button type="button" onClick={(event) => openTaskDetails(event, task._id)} style={taskNameButtonStyle}>
                  {task.name || task.task}
                </button>
              </td>
              <td style={{ ...tdStyle, textAlign: "center" }}><div style={{ display: "flex", justifyContent: "center" }}><AvatarGroup assignees={task.resources || []} /></div></td>
              <td style={{ ...tdStyle, textAlign: "center" }}>{formatReportDate(task.dueDate)}</td>
              <td style={{ ...tdStyle, textAlign: "center" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot }} />
                  <span style={{ color: "rgba(0,0,0,0.6)" }}>{task.status || task.statusLabel}</span>
                </div>
              </td>
              <td style={{ ...tdStyle, textAlign: "center" }}><PriorityBadge level={(task.priority || "").toUpperCase()} /></td>
<td style={{ ...tdStyle, textAlign: "center" }}><ProgressBar pct={task.completion ?? 0} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
  );

  const renderTimesheetRows = () => (
    <div className="reports-table-scroll">
    <table className="reports-data-table" style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={thStyle}>Task</th>
          <th style={thStyle}>Resources</th>
          <th style={thStyle}>Estimated Hrs</th>
          <th style={thStyle}>Delay Hrs</th>
          <th style={{ ...thStyle, textAlign: "right" }}>Auto-Submitted</th>
        </tr>
      </thead>
      <tbody>
        {currentRows.map((task, i) => (
          <tr
            key={task._id || i}
            style={{
              background:
                timesheetTaskId === task._id ? "#eef2ff" : "transparent",
              cursor: "pointer",
            }}
            onClick={() =>
              setTimesheetTaskId((prev) =>
                prev === task._id ? null : task._id,
              )
            }
          >
            <td style={{ ...tdStyle, color: "BLACk", fontWeight: 500 }}>
              {task.name || task.task}
            </td>
            <td style={tdStyle}>
              <AvatarGroup assignees={task.resources || []} />
            </td>
            <td style={tdStyle}>
              {task.estimatedHours ? `${task.estimatedHours}h` : "—"}
            </td>
            <td style={tdStyle}>
              {task.daysOverdue > 0 ? `${task.daysOverdue * 8}h` : "—"}
            </td>
            <td style={{ ...tdStyle, textAlign: "right" }}>
              <span
                style={{
                  color: task.loggedHours > 0 ? "#ef4444" : "#9ca3af",
                  fontWeight: 500,
                }}
              >
                {task.loggedHours > 0 ? "1 Entry" : "0 Entries"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );

  const renderRiskRows = () => (
    <div className="reports-table-scroll">
    <table className="reports-data-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "25%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "12%" }} />
        <col style={{ width: "12%" }} />
        <col style={{ width: "7%" }} />
      </colgroup>
      <thead><tr>
        <th style={thStyle}>Task</th>
        <th style={thStyle}>Resources</th>
        <th style={thStyle}>Due Date</th>
        <th style={thStyle}>Days Overdue</th>
        <th style={{ ...thStyle, textAlign: "center" }}>Impact</th>
        <th style={{ ...thStyle, textAlign: "center" }}>Priority</th>
        <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
      </tr></thead>
      <tbody>
        {currentRows.map((task) => {
          const sel = selectedTaskId === task._id;
          return (
            <tr key={task._id} style={{ background: sel ? "#eef2ff" : "transparent" }}>
              <td style={{ ...tdStyle, fontWeight: 500, color: "#1f2937" }}>
                <button type="button" onClick={(event) => openTaskDetails(event, task._id)} style={taskNameButtonStyle}>
                  {task.name || task.task}
                </button>
              </td>
              <td style={tdStyle}><AvatarGroup assignees={task.resources || []} /></td>
              <td style={tdStyle}>{formatReportDate(task.dueDate)}</td>
              <td style={tdStyle}>{task.daysOverdueDisplay || `${task.daysOverdue ?? 0} Days`}</td>
              <td style={{ ...tdStyle, textAlign: "center" }}><ImpactBadge level={task.impact || "LOW"} /></td>
              <td style={{ ...tdStyle, textAlign: "center" }}><PriorityBadge level={(task.priority || "HIGH").toUpperCase()} /></td>
              <td style={tdStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444" }} />
                  <span style={{ fontSize: 13, color: "rgba(0,0,0,0.6)" }}>At Risk</span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
  return (
    <div className={`reports-page ${(selectedTaskId || timesheetTaskId) ? "reports-page--with-details" : ""}`}
      style={{
        padding: "20px clamp(12px, 4vw, 32px)",
        paddingRight: taskDrawerWidth ? `calc(clamp(12px, 4vw, 32px) + ${taskDrawerWidth}px)` : undefined,
        minHeight: "100vh",
        background: "#f3f4f8",
      }}
    >
      <div
        className={`reports-project-layout grid grid-cols-1 gap-6 ${(selectedTaskId || timesheetTaskId) ? "reports-project-layout--with-details" : ""}`}
        style={{ alignItems: "stretch" }}
      >
        <div>
          {/* Header */}
  {/* Header */}
          <div className="reports-header" style={{ background: "#fff", borderRadius: 16, boxShadow: "0px 0px 4px rgba(0,0,0,0.05)", padding: "24px 28px", marginBottom: 20 }}>
            <div className="reports-header-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <button onClick={() => { onBack(); dispatch(clearProjectDetail()); }}
                  style={{ width: 52, height: 53, borderRadius: 8, border: "none", background: "#EEF1FF", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                  <ArrowLeft size={20} color="#4379FE" />
                </button>
                <div>
                  <h2 style={{ margin: 0, fontFamily: FONT, fontWeight: 500, fontSize: 24, lineHeight: "36px", textTransform: "capitalize", color: "#000" }}>Project Report</h2>
                  <p style={{ margin: "4px 0 0", fontFamily: FONT, fontWeight: 400, fontSize: 12, lineHeight: "18px", textTransform: "capitalize", color: "rgba(0,0,0,0.5)" }}>View Project Insights And Performance Analytics</p>
                </div>
              </div>
              <button type="button" onClick={handleExportCSV} disabled={!currentProjectId}
                style={{ boxSizing: "border-box", width: 163, height: 40, border: "1px solid #4253C7", borderRadius: 8, background: "#fff", color: "#4253C7", fontFamily: FONT, fontStyle: "normal", fontWeight: 400, fontSize: 14, lineHeight: "21px", display: "flex", alignItems: "center", justifyContent: "center", cursor: currentProjectId ? "pointer" : "not-allowed", opacity: currentProjectId ? 1 : 0.5 }}>
                Export CSV
              </button>
            </div>

            <div className="reports-picker-area" style={{ border: "1px solid rgba(199,199,199,0.23)", boxShadow: "0px 0px 4px rgba(0,0,0,0.05)", borderRadius: 8, padding: "27px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 24 }}>
             {/* Project picker */}
<div onClick={() => setShowDropdown((p) => !p)}
  style={{ display: "flex", alignItems: "center", gap: 12, position: "relative", width: "100%", maxWidth: 478, minWidth: 220, flex: "1 1 280px", height: 37, boxSizing: "border-box", border: "1px solid rgba(199,199,199,0.23)", boxShadow: "0px 0px 4px rgba(0,0,0,0.05)", borderRadius: 8, padding: "0 12px", background: "#fff", cursor: "pointer" }}>
  <Icons.ProjectSearch />
  <span style={{ flex: 1, fontFamily: FONT, fontSize: 12, lineHeight: "18px", textTransform: "capitalize", color: "#7F7F7F", minWidth: 120 }}>{selectedProject?.name || (projectsLoading ? "Loading..." : "No Projects")}</span>
  <ChevronDown size={15} color="#000" />
  {showDropdown && (
    <>
    <div
      onClick={(e) => { e.stopPropagation(); setShowDropdown(false); }}
      style={{ position: "fixed", inset: 0, zIndex: 19 }}
    />
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 10px 24px rgba(0,0,0,0.08)", zIndex: 20, overflow: "hidden" }}>
      <div style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
        <input
          autoFocus
          type="text"
          value={projectSearch}
          onChange={(e) => setProjectSearch(e.target.value)}
          placeholder="Search projects..."
          style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13, fontFamily: FONT, outline: "none" }}
        />
      </div>
      <div style={{ maxHeight: 240, overflowY: "auto" }}>
        {projects
          .filter((p) => (p.name || "").toLowerCase().includes(projectSearch.toLowerCase()))
          .map((p) => {
            const pid = p._id || p.id;
            return (
              <button key={pid} type="button"
                onClick={(e) => { e.stopPropagation(); handleProjectChange(pid); setProjectSearch(""); }}
                style={{ width: "100%", padding: "10px 14px", border: "none", background: String(pid) === String(currentProjectId) ? "#eef2ff" : "#fff", color: "#374151", fontSize: 13, textAlign: "left", cursor: "pointer" }}>
                {p.name}
              </button>
            );
          })}
        {projects.filter((p) => (p.name || "").toLowerCase().includes(projectSearch.toLowerCase())).length === 0 && (
          <div style={{ padding: "12px 14px", fontSize: 13, color: "#9ca3af", fontFamily: FONT }}>No projects found</div>
        )}
      </div>
    </div>
    </>
  )}
</div>
              {/* Completion bar */}
      <div style={{ flex: "1 1 220px", minWidth: 180, maxWidth: 339, marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
  <span style={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e", marginBottom: 4 }}>{completionPct}%</span>
  <div style={{ width: "100%", height: 7, background: "rgba(198, 198, 198, 0.36)", borderRadius: 68, overflow: "hidden" }}>
                  <div style={{ width: `${completionPct}%`, height: "100%", background: "#528CF9", borderRadius: 68 }} />
                </div>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9ca3af" }}>{completedTasks} Of {totalTasks} Tasks Completed</p>
              </div>
            </div>
          </div>

          {/* Stat cards */}
          <div className="reports-stats" style={{ gap: 16, marginBottom: 20 }}>
            <StatCard label="Total Tasks"   value={totalTasks}     sub="All tasks"              borderColor="#6366f1" />
            <StatCard label="Pending Tasks" value={pendingTasks}   sub="Awaiting Action"        borderColor="#f59e0b" />
            <StatCard label="At Risk"       value={atRiskCount}    sub="Needs Immediate action" borderColor="#ef4444" />
            <StatCard label="Resources"     value={resourceCount}  sub="Working on project"     borderColor="#22c55e" />
          </div>

          {/* Tabs + table card — restyled to match the supplied design */}
          <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 0 4px rgba(0,0,0,0.05)", overflow: "hidden" }}>
            <div className="reports-table-card-head" style={{ padding: "24px 28px 0" }}>
              <div className="reports-tabs" style={{ display: "flex", gap: 32, borderBottom: "1px solid rgba(127,127,127,0.07)" }}>
                {["Status", "Timesheet", "Risk"].map((tab) => (
                  <button key={tab} onClick={() => handleTabChange(tab)}
                    style={{
                      padding: "0 0 18px 0",
                      border: "none",
                      borderBottom: activeTab === tab ? "2px solid #4253C7" : "2px solid transparent",
                      background: "none",
                      cursor: "pointer",
                      fontFamily: FONT,
                      fontSize: 14,
                      fontWeight: activeTab === tab ? 500 : 400,
                      color: activeTab === tab ? "#4253C7" : "rgba(0,0,0,0.5)",
                      textTransform: "capitalize",
                      marginBottom: -1,
                    }}>
                    {tab}
                  </button>
                ))}
              </div>

           <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
  <h3 style={{ margin: "24px 0 20px", fontSize: 16, fontWeight: 500, color: "#000", fontFamily: FONT }}>
    {TAB_LABELS[activeTab]}({currentPag?.totalResults ?? currentRows.length})
  </h3>
  {activeTab === "Timesheet" && (
    <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 14px", cursor: "pointer", background: "#fafafa", marginTop: 12 }}>
      <Calendar size={14} color="#6366f1" />
      <span style={{ fontSize: 13, color: "#374151" }}>{weekDateRange}</span>
      <ChevronDown size={14} color="#89a9e0" />
    </div>
  )}
</div>
            </div>

            <div className="reports-table-body" style={{ padding: "0 28px" }}>
              {isTabLoading ? <LoadingSpinner text="Loading tasks..." /> : (
                <>
                  {currentRows.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af" }}>
                      <p style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>
                        {activeTab === "Risk" ? "No At Risk Tasks" : "No tasks found"}
                      </p>
                    </div>
                  ) : (
                    <>
                      {activeTab === "Status"    && renderStatusRows()}
                      {activeTab === "Timesheet" && renderTimesheetRows()}
                      {activeTab === "Risk"      && renderRiskRows()}
                    </>
                  )}
                </>
              )}
            </div>

            {!isTabLoading && currentRows.length > 0 && (
              <div className="reports-pagination" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, padding: "20px 28px", background: "#F5F5F5" }}>
                <span style={{ fontSize: 13, color: "rgba(0,0,0,0.53)", fontFamily: FONT }}>
                  Showing {currentPag?.showing?.from ?? 1} to {currentPag?.showing?.to ?? currentRows.length} of {currentPag?.totalResults ?? currentRows.length} results
                </span>
                <Pagination current={page} total={totalPages} onChange={handlePageChange} />
              </div>
            )}
          </div>
        </div>

        {(selectedTaskId && activeTab !== "Timesheet") || (activeTab === "Timesheet" && timesheetTaskId && currentProjectId) ? (
          <div className="reports-task-details-column">
          {selectedTaskId && activeTab !== "Timesheet" ? (
            taskDetailLoading ? (
              <div style={{ height: "100%", background: "#fff", borderRadius: 16, padding: 32 }}><LoadingSpinner text="Loading task..." /></div>
            ) : taskDetail ? (
              <TaskDetailsPanel
                task={taskDetail}
                projectName={selectedProject?.name}
                projectTaskProgress={`${completedTasks}/${totalTasks}`}
                onClose={() => { setSelectedTaskId(null); dispatch(clearTaskDetail()); }}
              />
            ) : null
          ) : (
          <TaskTimesheetPanel
            projectId={currentProjectId}
            taskId={timesheetTaskId}
            taskName={currentRows.find((r) => r._id === timesheetTaskId)?.name || "Task"}
            projectName={selectedProject?.name || ""}
            onClose={() => setTimesheetTaskId(null)}
          />
          )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// LEAVE REPORT PAGE
// ════════════════════════════════════════════════════════════════════
const LEAVE_ALLOC = { CL: 10, ML: 5, RL: 2 };

const LeaveReportPage = ({ onBack }) => {
  const currentYear = new Date().getFullYear();

  const [resources, setResources]       = useState([]);
  const [allRows, setAllRows]           = useState([]);   // every leave record for the selected user/year, unpaginated
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [downloading, setDownloading]   = useState(false);
  const [loadError, setLoadError]       = useState("");

  const [selectedYear,     setSelectedYear]     = useState(String(currentYear));
  const [showYearDrop,     setShowYearDrop]     = useState(false);
  const [selectedUserId,   setSelectedUserId]   = useState("");    // "" = all resources
const [selectedResource, setSelectedResource] = useState("-");
  const [showResourceDrop, setShowResourceDrop] = useState(false);
  const [resourceSearch, setResourceSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 5;
    const ROW_HEIGHT = 64; 

  // Load the resource (employee) picker once — same endpoint the
  // Leave Management "Manage" view uses to populate its filter.
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch("/api/reports/leave/resources");
        const json = await res.json();
        if (json?.data) setResources(json.data);
      } catch {
        // Resource picker is non-critical — leave it empty on failure.
      }
    })();
  }, []);

  // Pull every leave record from the real leave-tracking API
  // (/api/leaves/history — the same source LeaveManagement.jsx uses),
  // then filter to the selected year/resource on the client so the
  // report always reflects live leave-track data.
const loadLeaveData = useCallback(async () => {
    if (!selectedUserId) {
      setAllRows([]);
      setLeaveLoading(false);
      setLoadError("");
      return;
    }
    setLeaveLoading(true);
    setLoadError("");
    try {
      const params = new URLSearchParams({ year: selectedYear, limit: 1000, userId: selectedUserId });
      const res = await authFetch(`/api/reports/leave?${params}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Could not load leave data.");
      setAllRows(json.data?.history || []);
    } catch {
      setAllRows([]);
      setLoadError("Could not load leave data.");
    } finally {
      setLeaveLoading(false);
    }
  }, [selectedYear, selectedUserId]);

  useEffect(() => {
    loadLeaveData();
  }, [loadLeaveData]);

  const handleResourceChange = (res, userId) => {
    setSelectedResource(res);
    setSelectedUserId(userId);
    setPage(1);
    setShowResourceDrop(false);
  };

  const handleDownload = async () => {
    if (!selectedUserId) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams({ year: selectedYear, userId: selectedUserId });
      const res = await authFetch(`/api/reports/leave/download?${params}`);
      const report = await res.json();
      if (!res.ok || !report?.success) throw new Error(report?.message || "Unable to download leave report.");
      downloadLeaveReportAsExcel(
        report.data?.leaves || [],
        report.data?.year || selectedYear,
        report.data?.resource?.name || selectedResource,
      );
    } catch (error) {
      console.error("Unable to download the leave report", error);
      setLoadError(error.message || "Unable to download leave report.");
    } finally {
      setDownloading(false);
    }
  };

  // Client-side pagination over the real, filtered rows
  const totalResults = allRows.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
  const leaveRows = allRows.slice((page - 1) * pageSize, page * pageSize);
  const pagination = {
    totalPages,
    totalResults,
    showing: {
      from: totalResults === 0 ? 0 : (page - 1) * pageSize + 1,
      to: Math.min(page * pageSize, totalResults),
    },
  };

  // Remaining balance computed from the real, approved leave-track
  // records for the selected resource/year.
  const remainingLeaves = Object.entries(LEAVE_ALLOC).map(([type, total]) => {
    const taken = allRows
      .filter((r) => (r.leaveType || r.type) === type && r.status === "APPROVED")
      .reduce((s, r) => s + (r.totalDays || r.days || 0), 0);
    return { type, left: Math.max(0, total - taken) };
  });

  const stats = {
    totalAllocated: selectedUserId ? Object.values(LEAVE_ALLOC).reduce((s, v) => s + v, 0) : 0,
    totalTaken: allRows.filter((r) => r.status === "APPROVED").reduce((s, r) => s + (r.totalDays || r.days || 0), 0),
    pendingCount: allRows.filter((r) => r.status === "PENDING").length,
    balance: 0,
  };
  stats.balance = stats.totalAllocated - stats.totalTaken;

  
  const leaveThStyle = { textAlign: "left", padding: "22px 32px", fontSize: 16, fontWeight: 400, color: "#717287", fontFamily: FONT, borderBottom: "1px solid #ececec" };
  const leaveTdStyle = { padding: "10px 32px", fontSize: 14, color: "black", fontFamily: FONT, fontWeight: 500, borderBottom: "1px solid #ececec" };
  const dropList = { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 50, overflow: "hidden" };
  const dropItem = (active) => ({ width: "100%", padding: "10px 14px", border: "none", background: active ? "#eef2ff" : "#fff", color: active ? "#3b5bdb" : "#374151", fontWeight: active ? 600 : 400, fontSize: 13, textAlign: "left", cursor: "pointer" });

  return (
    <div className="reports-page"
      style={{
        padding: "28px 32px",
        minHeight: "100vh",
        background: "#f3f4f8",
      }}
    >
     {/* Header */}
      <div className="reports-header reports-leave-header" style={{ background: "#fff", borderRadius: 16, boxShadow: "0px 0px 4px rgba(0,0,0,0.05)", padding: "24px 28px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack}
            style={{ width: 52, height: 53, borderRadius: 8, border: "none", background: "#EEF1FF", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
            <ArrowLeft size={20} color="#4379FE" />
          </button>
          <div>
            <h2 style={{ margin: 0, fontFamily: FONT, fontWeight: 500, fontSize: 24, lineHeight: "36px", textTransform: "capitalize", color: "#000" }}>Leave Report</h2>
            <p style={{ margin: "4px 0 0", fontFamily: FONT, fontWeight: 400, fontSize: 12, lineHeight: "18px", textTransform: "capitalize", color: "rgba(0,0,0,0.5)" }}>Overview Of Leave Balance And Leaves Taken By Each Resource</p>
          </div>
        </div>
<div className="reports-leave-controls" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {/* Resource dropdown */}
<div style={{ position: "relative" }}>
  <button onClick={() => { setShowResourceDrop((p) => !p); setShowYearDrop(false); }}
    style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", width: "100%", maxWidth: 294, minWidth: 220, flex: "1 1 220px", height: 54, boxSizing: "border-box", border: "1px solid rgba(199,199,199,0.23)", boxShadow: "0px 0px 4px rgba(0,0,0,0.05)", borderRadius: 8, padding: "0 36px 0 12px", background: "#fff", cursor: "pointer", position: "relative" }}>
    <span style={{ fontFamily: FONT, fontSize: 10, lineHeight: "15px", textTransform: "capitalize", color: "#7F7F7F" }}>Resource</span>
    <span style={{ fontFamily: FONT, fontSize: 12, lineHeight: "18px", textTransform: "capitalize", color: "#000000" }}>{selectedResource}</span>
    <ChevronDown size={14} color="#000" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }} />
  </button>
  {showResourceDrop && (
    <>
    <div
      onClick={() => setShowResourceDrop(false)}
      style={{ position: "fixed", inset: 0, zIndex: 19 }}
    />
    <div onClick={(e) => e.stopPropagation()} style={dropList}>
      <div style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
        <input
          autoFocus
          type="text"
          value={resourceSearch}
          onChange={(e) => setResourceSearch(e.target.value)}
          placeholder="Search resources..."
          style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13, fontFamily: FONT, outline: "none" }}
        />
      </div>
      <div style={{ maxHeight: 240, overflowY: "auto" }}>
        <button type="button" onClick={() => { handleResourceChange("-", ""); setResourceSearch(""); }} style={dropItem(selectedResource === "-")}>-</button>
        {resources
          .filter((r) => (r.name || "").toLowerCase().includes(resourceSearch.toLowerCase()))
          .map((r) => (
            <button key={r._id} type="button"
              onClick={() => { handleResourceChange(r.name, r._id); setResourceSearch(""); }} style={dropItem(selectedResource === r.name)}>
              {r.name}
            </button>
          ))}
        {resources.filter((r) => (r.name || "").toLowerCase().includes(resourceSearch.toLowerCase())).length === 0 && (
          <div style={{ padding: "12px 14px", fontSize: 13, color: "#9ca3af", fontFamily: FONT }}>No resources found</div>
        )}
      </div>
    </div>
    </>
  )}
</div>
          {/* Year dropdown */}
          <div style={{ position: "relative" }}>
            <button onClick={() => { setShowYearDrop((p) => !p); setShowResourceDrop(false); }}
              style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", width: "100%", maxWidth: 294, minWidth: 220, flex: "1 1 220px", height: 54, boxSizing: "border-box", border: "1px solid rgba(199,199,199,0.23)", boxShadow: "0px 0px 4px rgba(0,0,0,0.05)", borderRadius: 8, padding: "0 36px 0 12px", background: "#fff", cursor: "pointer", position: "relative" }}>
              <span style={{ fontFamily: FONT, fontSize: 10, lineHeight: "15px", textTransform: "capitalize", color: "#7F7F7F" }}>Year</span>
              <span style={{ fontFamily: FONT, fontSize: 12, lineHeight: "18px", textTransform: "capitalize", color: "#000000" }}>{selectedYear}</span>
              <ChevronDown size={14} color="#000" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }} />
            </button>
            {showYearDrop && (
              <>
                <div
                  onClick={() => setShowYearDrop(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 19 }}
                />
                <div style={dropList}>
                  {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                    <button key={y} type="button"
                      onClick={() => { setSelectedYear(String(y)); setPage(1); setShowYearDrop(false); }}
                      style={dropItem(String(y) === selectedYear)}>{y}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="reports-stats" style={{ gap: 16, marginBottom: 20 }}>
        <StatCard label="Total Leaves"     value={stats.totalAllocated} sub="Total Leaves Allocated" borderColor="#ffff" />
        <StatCard label="Leaves Taken"     value={stats.totalTaken}     sub="Total Leaves Taken"     borderColor="#3b5bdb" />
        <StatCard label="Pending Requests" value={stats.pendingCount}   sub="Awaiting Approval"      borderColor="#ef4444" />
        <StatCard label="Leave Balance"    value={stats.balance}        sub="Total Leave Balance"    borderColor="#22c55e" />
      </div>

      {/* Body */}
      {/* Body */}
<div className={`grid gap-6 items-stretch ${selectedUserId ? "grid-cols-1 xl:grid-cols-[minmax(0,1fr)_460px]" : "grid-cols-1"}`}>
  <div className="reports-leave-body" style={{ background: "#fff", border: "1px solid #EBEBEB", borderRadius: 8, boxShadow: "0px 6px 16px rgba(0,0,0,0.001)", padding: 24, display: "flex", flexDirection: "column" }}>
          {selectedUserId && (
            <h3 style={{ margin: "0 0 24px", fontSize: 20, fontWeight: 500, color: "#000", fontFamily: FONT, textTransform: "capitalize" }}>Leave Summary</h3>
          )}

          <div style={{ border: "1px solid rgba(0,0,0,0.17)", borderRadius: 16, boxShadow: "0px 0px 4px rgba(0,0,0,0.05)", overflow: "hidden" }}>
            {!selectedUserId ? (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                minHeight: ROW_HEIGHT * pageSize + 64, padding: "48px 24px",
                gap: 10,
              }}>
                <FileText size={24} color="rgba(127, 127, 127, 0.69)" strokeWidth={1.8} />
                <span style={{
                  fontFamily: FONT, fontWeight: 500, fontSize: 20, lineHeight: "30px",
                  textTransform: "capitalize", color: "rgba(127, 127, 127, 0.69)",
                }}>
                  No resource selected. Please choose a resource to view the leave report.
                </span>
              </div>
            ) : leaveLoading ? (
              <div style={{ padding: "24px" }}><LoadingSpinner text="Loading leave data..." /></div>
            ) : (
              <>
               {loadError && <p style={{ margin: 0, padding: "16px 24px 0", fontSize: 13, color: "#ef4444" }}>{loadError}</p>}
<div className="reports-table-scroll" style={{ height: ROW_HEIGHT * pageSize + 64, overflowY: "auto", position: "relative" }}>                  
   <table className="reports-data-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
  <colgroup>
    <col style={{ width: "20%" }} />
    <col style={{ width: "20%" }} />
    <col style={{ width: "20%" }} />
    <col style={{ width: "20%" }} />
    <col style={{ width: "20%" }} />
  </colgroup>
  <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}><tr>
    <th style={leaveThStyle}>From</th>
    <th style={leaveThStyle}>To</th><th style={leaveThStyle}>Days</th>
    <th style={leaveThStyle}>Type</th><th style={leaveThStyle}>Status</th>
  </tr></thead>
                    <tbody>
                      {leaveRows.map((row, i) => (
                        <tr key={row._id || i} style={{ height: ROW_HEIGHT }}>
                          <td style={leaveTdStyle}>{formatShortDate(row.fromDate || row.from)}</td>
                          <td style={leaveTdStyle}>{formatShortDate(row.toDate || row.to)}</td>
                          <td style={leaveTdStyle}>{row.totalDays ?? row.days ?? "-"}</td>
                          <td style={leaveTdStyle}>{row.leaveType || row.type}</td>
                          <td style={leaveTdStyle}><LeaveBadge status={row.status} /></td>
                        </tr>
                      ))}

                 {Array.from({ length: Math.max(0, pageSize - leaveRows.length) }).map((_, i) => (
                        <tr key={`empty-${i}`} style={{ height: ROW_HEIGHT }}>
                          <td
                            colSpan={5}
                            style={{
                              ...leaveTdStyle,
                              borderBottom: leaveRows.length === 0 ? "none" : "1px solid #ececec",
                              color: "transparent",
                            }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                      ))}

                      {leaveRows.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ border: "none", padding: 0 }}>
                            <div
                              style={{
                                position: "absolute",
                                top: 64,
                                left: 0,
                                right: 0,
                                height: ROW_HEIGHT * pageSize,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#9ca3af",
                                fontSize: 14,
                                fontFamily: FONT,
                                pointerEvents: "none",
                              }}
                            >
                              No leave records found.
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

            <div className="reports-pagination" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: 67, padding: "0 24px", background: "#F5F5F5", borderTop: "1px solid #F1F1F1", color: "#6B7280", fontSize: 12 }}>
                  <span style={{ fontFamily: FONT }}>
                    Showing {pagination.showing.from} To {pagination.showing.to} Of {pagination.totalResults} Results
                  </span>
                  <Pagination current={page} total={totalPages} onChange={setPage} />
                </div>
              </>
            )}
          </div>
        </div>
           {selectedUserId && (
  <div style={{ background: "#fff", border: "1px solid #EBEBEB", borderRadius: 8, padding: 24, display: "flex", flexDirection: "column", height: "100%", boxShadow: "0px 6px 16px rgba(0,0,0,0.001)" }}>
          <h3 style={{ margin: "0 0 24px", fontSize: 20, fontWeight: 500, color: "#000", fontFamily: FONT, flexShrink: 0, textTransform: "capitalize" }}>Remaining Leaves</h3>
          <div style={{ border: "1px solid #EBEBEB", boxShadow: "0px 0px 4px rgba(0,0,0,0.05)", borderRadius: 16, overflow: "hidden", flexShrink: 0, marginBottom: 24 }}>
            {remainingLeaves.map((item, i) => (
              <div key={item.type} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", height: 48, borderBottom: i < remainingLeaves.length - 1 ? "1px solid rgba(0,0,0,0.08)" : "none" }}>
                <span style={{ fontSize: 16, color: "#717287", fontFamily: FONT, fontWeight: 400 }}>{item.type}</span>
                <span style={{ fontSize: 20, fontWeight: 400, color: "#000", fontFamily: FONT }}>{item.left} Left</span>
              </div>
            ))}
          </div>
<div style={{ flex: 1 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 16, flexShrink: 0, paddingBottom: 8 }}>
            <button onClick={handleDownload} disabled={downloading || leaveRows.length === 0}
              style={{ width: "100%", height: 40, border: "1px solid #4253C7", borderRadius: 8, background: "#4253C7", color: "#fff", fontWeight: 400, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontFamily: FONT, opacity: downloading ? 0.7 : 1, marginBottom: 4 }}>
              {downloading ? <Loader size={18} style={{ animation: "spin 1s linear infinite" }} /> : <Download size={18} />}
              Download
            </button>
            <button onClick={onBack}
              style={{ width: "100%", height: 40, border: "1px solid #4253C7", borderRadius: 8, background: "#fff", color: "#4253C7", fontWeight: 400, fontSize: 14, cursor: "pointer", fontFamily: FONT, marginBottom: 8 }}>
              Back
            </button>
          </div>
        </div>
        )}      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// ROOT ENTRY POINT
// ════════════════════════════════════════════════════════════════════
const Reports = () => {
  const dispatch = useDispatch();
  const [view, setView] = useState("main");
  const [initialTab, setInitialTab] = useState("Status");

  useEffect(() => {
    dispatch(fetchReportProjects());
  }, [dispatch]);

  useEffect(() => {
    document.querySelector(".page-content")?.scrollTo({ top: 0 });
  }, [view]);

  return (
    <>
      <style>{REPORTS_RESPONSIVE_STYLES}</style>
      {view === "project" && (
        <ProjectReportPage onBack={() => setView("main")} initialTab={initialTab} />
      )}
      {view === "leave" && <LeaveReportPage onBack={() => setView("main")} />}
      {view === "main" && (
        <ReportsMain
          onProjectReport={(tab = "Status") => { setInitialTab(tab); setView("project"); }}
          onLeaveReport={() => setView("leave")}
        />
      )}
    </>
  );
};

export default Reports;
